/**
 * Shared filter bar for the Order Book and Trade Book.
 *
 * Dates are calendar days in the application's display timezone (IST). The backend resolves
 * TODAY / YESTERDAY itself and filters on the stored IST date fields, so the browser's own clock
 * and timezone never decide which records a user sees.
 */
import React, { useEffect, useState } from "react";
import { Search } from "lucide-react";

export const PERIODS = [["ALL", "All dates"], ["LAST_7", "Last 7 days"], ["TODAY", "Today"], ["YESTERDAY", "Yesterday"], ["CUSTOM", "Custom"]];

/** YYYY-MM-DD in IST, `daysAgo` days before now. */
export function istDay(daysAgo = 0, now = new Date()) {
  const ist = new Date(now.getTime() + 5.5 * 3600 * 1000 - daysAgo * 86400 * 1000);
  return ist.toISOString().slice(0, 10);
}

// Orders and fills open on the LAST 7 DAYS (UI/UX audit Phase 4): "Today" looked empty at night and before
// the first fill of the morning, as if nothing had ever traded.
export const DEFAULT_REPORT_FILTERS = {
  period: "LAST_7", date_from: "", date_to: "", symbol: "", exchange: "ALL", side: "ALL",
  environment: "ALL", trade_id: "",
};

/** Query params for /orders and /trade-book/*: drops empty values and incomplete custom ranges. */
export function reportParams(f, extra = {}) {
  const p = { ...extra };
  if (f.period && f.period !== "ALL") {
    if (f.period === "LAST_7") {
      // sent as the IST date range the backend already understands
      p.period = "CUSTOM";
      p.date_from = istDay(6);
      p.date_to = istDay(0);
    } else if (f.period === "CUSTOM") {
      if (!f.date_from && !f.date_to) return null;          // nothing chosen yet: don't query
      p.period = "CUSTOM";
      if (f.date_from) p.date_from = f.date_from;
      if (f.date_to) p.date_to = f.date_to;
    } else {
      p.period = f.period;
    }
  }
  if (f.symbol) p.symbol = f.symbol;
  for (const k of ["exchange", "side", "environment", "segment"]) {
    if (f[k] && f[k] !== "ALL") p[k] = f[k];
  }
  if (f.trade_id) p.trade_id = f.trade_id;
  return p;
}

export function Sel({ label, value, onChange, options, testid }) {
  return (
    <label className="flex items-center gap-1.5 text-[11px]">
      <span className="text-slate-500">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="bg-[#0A0C10] border border-[#2A2F3A] rounded-sm px-1.5 py-1 text-[11px] mono outline-none focus:border-[#2563EB]">
        {options.map((o) => {
          const [v, l] = Array.isArray(o) ? o : [o, o];
          return <option key={v} value={v}>{l}</option>;
        })}
      </select>
    </label>
  );
}

const inputCls = "bg-[#0A0C10] border border-[#2A2F3A] rounded-sm px-2 py-1 text-[11px] mono outline-none focus:border-[#2563EB]";

export default function ReportFilters({ value, onChange, trades = [], showSegment = false, hideEnvironment = false, children }) {
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  const [symbol, setSymbol] = useState(value.symbol || "");

  // debounce free-text symbol search
  useEffect(() => {
    const t = setTimeout(() => { if (symbol !== value.symbol) onChange({ ...value, symbol }); }, 400);
    return () => clearTimeout(t);
  }, [symbol]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="term-panel rounded-sm p-2.5 flex flex-wrap items-center gap-3" data-testid="report-filters">
      <div className="flex rounded-sm overflow-hidden border border-[#2A2F3A]" data-testid="filter-period">
        {PERIODS.map(([k, l]) => (
          <button key={k} onClick={() => set("period")(k)} data-testid={`period-${k.toLowerCase()}`}
            className={`px-2.5 py-1 text-[11px] font-bold ${value.period === k ? "bg-[#2563EB] text-white" : "bg-[#0A0C10] text-slate-400 hover:text-slate-200"}`}>
            {l}
          </button>
        ))}
      </div>
      {value.period === "CUSTOM" && (
        <div className="flex items-center gap-1.5 text-[11px]">
          <input type="date" value={value.date_from} max={value.date_to || undefined}
            onChange={(e) => set("date_from")(e.target.value)} data-testid="filter-date-from" className={inputCls} />
          <span className="text-slate-500">to</span>
          <input type="date" value={value.date_to} min={value.date_from || undefined}
            onChange={(e) => set("date_to")(e.target.value)} data-testid="filter-date-to" className={inputCls} />
          <span className="text-slate-600">IST</span>
        </div>
      )}
      {!hideEnvironment && <Sel label="Env" value={value.environment} onChange={set("environment")} testid="filter-env"
        options={[["ALL", "All"], "PAPER", "SHADOW", "LIVE"]} />}
      <Sel label="Exchange" value={value.exchange} onChange={set("exchange")} testid="filter-exchange"
        options={[["ALL", "All"], "MCX", "NFO", "BFO"]} />
      {showSegment && (
        <Sel label="Segment" value={value.segment || "ALL"} onChange={set("segment")} testid="filter-segment"
          options={[["ALL", "All"], "MCX", "NFO"]} />
      )}
      <Sel label="Side" value={value.side} onChange={set("side")} testid="filter-side"
        options={[["ALL", "All"], "BUY", "SELL"]} />
      <Sel label="Trade" value={value.trade_id} onChange={set("trade_id")} testid="filter-trade"
        options={[["", "All trades"], ...trades]} />
      {children}
      <label className="flex items-center gap-1.5 text-[11px] ml-auto">
        <Search className="h-3.5 w-3.5 text-slate-500" />
        <input value={symbol} onChange={(e) => setSymbol(e.target.value)} placeholder="symbol"
          data-testid="filter-symbol" className={`w-40 ${inputCls}`} />
      </label>
    </div>
  );
}
